import { Application, Container, Ticker } from "pixi.js";

export interface Size {
  width: number;
  height: number;
}

export interface SafeArea {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * A screen of the game. The engine owns the stage and destroys the view when the scene is replaced.
 *
 * Rendering is on demand: the engine draws a frame only when a scene asks for one, so a still
 * screen costs no CPU or GPU. Scenes call the `requestRender` they get in `attach` after any
 * visual change, and return true from `update` while an animation still needs frames.
 */
export interface Scene {
  readonly view: Container;
  layout(size: Size, safe: SafeArea): void;
  attach?(requestRender: () => void): void;
  /** Advances animations; returns true while more frames are needed. */
  update?(deltaMs: number): boolean | void;
  destroy?(): void;
}

/** Long gaps (e.g. after an idle period) must not make animations jump. */
const MAX_FRAME_DELTA_MS = 100;

export class Engine {
  private scene: Scene | undefined;
  private active = true;
  private contextLost = false;
  private frame: number | undefined;
  private lastFrameAt: number | undefined;
  private readonly requestRender = () => this.schedule();

  private constructor(readonly app: Application) {}

  static async create(container: HTMLElement): Promise<Engine> {
    // Pixi's shared system ticker would otherwise run a requestAnimationFrame loop forever
    // (only for pointer hover checks, which a touch game does not need).
    Ticker.system.autoStart = false;
    Ticker.system.stop();

    const app = new Application();
    const dpr = window.devicePixelRatio || 1;
    await app.init({
      resizeTo: container,
      // No continuous render loop: Engine draws frames on demand.
      autoStart: false,
      // WebGL is the stable path in mobile WebViews; WebGPU is still experimental in Pixi.
      preference: "webgl",
      powerPreference: "low-power",
      background: "#1b1e2b",
      // At 2x resolution edges are already smooth; MSAA would only cost fill rate.
      antialias: dpr < 2,
      autoDensity: true,
      // Cap the resolution to keep fill rate and memory sane on high-DPI phones.
      resolution: Math.min(dpr, 2),
    });
    app.ticker.stop();
    container.appendChild(app.canvas);

    const engine = new Engine(app);
    app.renderer.on("resize", () => {
      engine.layout();
      engine.schedule();
    });
    app.canvas.addEventListener("webglcontextlost", () => engine.setContextLost(true));
    // Pixi re-uploads GPU resources on restore; we only redraw.
    app.canvas.addEventListener("webglcontextrestored", () => engine.setContextLost(false));
    return engine;
  }

  setScene(scene: Scene): void {
    const previous = this.scene;
    this.scene = scene;
    this.app.stage.addChild(scene.view);
    scene.attach?.(this.requestRender);
    this.layout();
    if (previous) {
      previous.destroy?.();
      previous.view.destroy({ children: true });
    }
    this.schedule();
  }

  /** Stop drawing while the app is in the background. */
  setActive(active: boolean): void {
    this.active = active;
    if (active) this.schedule();
    else this.cancel();
  }

  private setContextLost(lost: boolean): void {
    this.contextLost = lost;
    if (lost) this.cancel();
    else this.schedule();
  }

  private schedule(): void {
    if (this.frame !== undefined || !this.active || this.contextLost) return;
    this.frame = requestAnimationFrame((now) => this.draw(now));
  }

  private cancel(): void {
    if (this.frame !== undefined) cancelAnimationFrame(this.frame);
    this.frame = undefined;
    this.lastFrameAt = undefined;
  }

  private draw(now: number): void {
    this.frame = undefined;
    const delta = this.lastFrameAt === undefined ? 0 : Math.min(now - this.lastFrameAt, MAX_FRAME_DELTA_MS);
    const animating = this.scene?.update?.(delta) === true;
    this.app.renderer.render(this.app.stage);
    if (animating) {
      this.lastFrameAt = now;
      this.schedule();
    } else {
      this.lastFrameAt = undefined;
    }
  }

  private layout(): void {
    const { width, height } = this.app.screen;
    this.scene?.layout({ width, height }, readSafeArea());
  }
}

let safeAreaProbe: HTMLElement | undefined;

/**
 * Resolves the safe-area insets from style.css. Custom properties are not computed to pixels,
 * so they are applied as padding on a hidden element and read back.
 */
function readSafeArea(): SafeArea {
  if (!safeAreaProbe) {
    safeAreaProbe = document.createElement("div");
    safeAreaProbe.className = "safe-area-probe";
    document.body.appendChild(safeAreaProbe);
  }
  const css = getComputedStyle(safeAreaProbe);
  return {
    top: parseFloat(css.paddingTop) || 0,
    right: parseFloat(css.paddingRight) || 0,
    bottom: parseFloat(css.paddingBottom) || 0,
    left: parseFloat(css.paddingLeft) || 0,
  };
}
